import { useCallback, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router';

import { isMobileViewport } from '~/utils/moduleVisibility';
import { toViewPath } from '~/utils/navigationContext';

type Latched = { key: string; mobile: boolean };

let latched: Latched | undefined;

// The viewport class a view was rendered in, fixed for the life of that view.
// It is read once per view key, the first time anything on the client asks,
// and every later reader for the same key gets that answer whatever the window
// does afterwards: the article view's manifest is sent once and deduplicated
// on view_id, so if a rotation across the breakpoint renumbered the modules,
// every impression and click after it would carry numbers the manifest never
// had. The key is the normalised view path — the same thing the navigation
// context advances a view_id on — and not the router's location key, which a
// hash-only navigation (the footer's back-to-top link) changes without minting
// a view. A
// new view reads the viewport afresh. The server has no viewport and answers
// false without latching, so nothing is held across requests.
export function isMobileViewportForView(key: string): boolean {
  if (typeof window === 'undefined') return false;

  if (latched?.key !== key) latched = { key, mobile: isMobileViewport() };

  return latched.mobile;
}

// What a view is keyed on for the latch: the same normalised path the
// navigation context advances a view_id on, so the two never disagree. It runs
// through toViewPath, so a param that does not change the render — a PDP's
// ?size= — holds the latch rather than re-arming it.
export function useViewKey(): string {
  const { pathname, search } = useLocation();

  return toViewPath(pathname, search);
}

function subscribe() {
  return () => {};
}

function getServerSnapshot() {
  return false;
}

// The latched answer as a value a render can depend on. useSyncExternalStore
// re-renders with the client's answer during hydration, before any
// IntersectionObserver callback can run, so nothing that reads it through props
// fires with the server's false; nothing is subscribed to because the answer
// cannot change until the view does, and then getSnapshot changes with it.
export default function useIsMobileViewport(): boolean {
  const key = useViewKey();
  const getSnapshot = useCallback(() => isMobileViewportForView(key), [key]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
